import { useEffect, useState } from "react";
import { Mail, RefreshCw, Send, Sparkles, X, Unplug, ChevronDown, ChevronUp, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const sydFmt = new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Sydney", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
const syd = (iso) => { try { return sydFmt.format(new Date(iso)); } catch { return "—"; } };
const btn = "inline-flex items-center gap-2 rounded-sm px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-50";
const CAT = { quote: "text-neon border-neon/40", assessment: "text-gold border-gold/40", callback: "text-electric border-electric/40", question: "text-zinc-200 border-white/20" };

const ConnectCard = ({ status, onSync, onDisconnect, syncing, isOwner }) => {
  const connect = async () => {
    try {
      const { data } = await api.get("/admin/gmail/connect-url", { params: { origin: window.location.origin } });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start Google sign-in"); }
  };
  return (
    <div className="rounded-lg border border-white/10 bg-obsidian-800 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="gmail-connect-card">
      <div className="flex items-center gap-4">
        <span className={`h-2.5 w-2.5 rounded-full ${status.connected ? "bg-neon shadow-neon" : "bg-zinc-600"}`} />
        <div>
          <p className="font-heading font-bold" data-testid="gmail-status-text">
            {status.connected ? <>Connected · <span className="text-neon">{status.email}</span></> : status.configured ? "Gmail not connected" : "Gmail credentials not added yet"}
          </p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
            {status.connected ? `Last check ${status.last_sync_at ? syd(status.last_sync_at) : "never"} · checks daily at 7:30am Sydney` : "Drafts replies to customer enquiries for your approval"}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {status.connected ? (
          <>
            <button onClick={onSync} disabled={syncing} data-testid="gmail-sync-btn" className={`${btn} bg-neon text-obsidian hover:bg-neon-hover`}>
              <RefreshCw size={15} className={syncing ? "animate-spin" : ""} /> {syncing ? "Checking inbox…" : "Check inbox now"}
            </button>
            {isOwner && <button onClick={onDisconnect} data-testid="gmail-disconnect-btn" className={`${btn} border border-white/15 text-zinc-300 hover:border-red-400 hover:text-red-300`}><Unplug size={15} /> Disconnect</button>}
          </>
        ) : isOwner ? (
          <button onClick={connect} disabled={!status.configured} data-testid="gmail-connect-btn" className={`${btn} bg-neon text-obsidian hover:bg-neon-hover`}><Mail size={15} /> Connect Gmail</button>
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid="gmail-owner-only-note">Only the owner can connect Gmail</span>
        )}
      </div>
    </div>
  );
};

const MessageCard = ({ m, onChange, isOwner }) => {
  const [draft, setDraft] = useState(m.draft || "");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [instruction, setInstruction] = useState("");
  useEffect(() => setDraft(m.draft || ""), [m.id, m.draft]);

  const saveDraft = async () => { if (draft !== (m.draft || "")) await api.patch(`/admin/inbox/${m.id}`, { draft }).catch(() => {}); };
  const act = async (kind, fn) => { setBusy(kind); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); } finally { setBusy(""); } };
  const send = () => act("send", async () => { await saveDraft(); await api.post(`/admin/inbox/${m.id}/send`); toast.success(`Reply sent to ${m.from_email}`); onChange(); });
  const regen = () => act("regen", async () => { const { data } = await api.post(`/admin/inbox/${m.id}/regenerate`, { instruction: instruction || null }); setDraft(data.draft); setInstruction(""); toast.success("New draft ready"); });
  const dismiss = () => act("dismiss", async () => { await api.post(`/admin/inbox/${m.id}/dismiss`); onChange(); });
  const body = m.body_text || m.snippet || "";

  return (
    <div className="rounded-lg border border-white/10 bg-obsidian-800 p-6" data-testid={`inbox-msg-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-sm border px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest ${CAT[m.category] || CAT.question}`}>{m.category || "enquiry"}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{syd(m.received_at)}</span>
            {m.status === "sent" && <span className="rounded-sm bg-neon/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-neon">Sent {syd(m.sent_at)}</span>}
          </div>
          <p className="mt-2 font-heading font-bold text-lg truncate" data-testid="inbox-msg-subject">{m.subject || "(no subject)"}</p>
          <p className="text-sm text-zinc-400 truncate">{m.from_name ? `${m.from_name} · ` : ""}{m.from_email}</p>
        </div>
      </div>
      {m.summary && <p className="mt-4 flex gap-2 text-sm text-zinc-300"><Sparkles size={15} className="mt-0.5 shrink-0 text-gold" />{m.summary}</p>}

      <button onClick={() => setOpen((o) => !o)} data-testid="inbox-toggle-original" className="mt-4 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Hide" : "Show"} original email
      </button>
      {open && <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap rounded-sm border border-white/10 bg-obsidian p-4 font-body text-sm text-zinc-300">{body}</pre>}

      {m.status !== "sent" && m.status !== "dismissed" ? (
        <>
          <label className="mt-5 block font-mono text-[10px] uppercase tracking-widest text-gold">{isOwner ? "AI reply draft — edit before sending" : "AI reply draft — edit; the owner approves & sends"}</label>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={saveDraft} rows={10} data-testid="inbox-draft"
                    className="mt-2 w-full bg-obsidian-700 border border-white/10 rounded-sm p-3 text-sm leading-relaxed outline-none focus:border-neon" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {isOwner ? (
              <button onClick={send} disabled={!!busy || !draft.trim()} data-testid="inbox-send-btn" className={`${btn} bg-neon text-obsidian hover:bg-neon-hover`}><Send size={15} /> {busy === "send" ? "Sending…" : "Approve & send"}</button>
            ) : (
              <span className={`${btn} cursor-default border border-gold/30 text-gold`} data-testid="inbox-awaiting-owner"><Lock size={14} /> Awaiting owner approval</span>
            )}
            <input value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Optional: tell the AI how to change it…" data-testid="inbox-regen-instruction"
                   className="min-w-[220px] flex-1 bg-obsidian-700 border border-white/10 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-neon" />
            <button onClick={regen} disabled={!!busy} data-testid="inbox-regen-btn" className={`${btn} border border-gold/40 text-gold hover:bg-gold/10`}><Sparkles size={15} /> {busy === "regen" ? "Drafting…" : "Regenerate"}</button>
            <button onClick={dismiss} disabled={!!busy} data-testid="inbox-dismiss-btn" className={`${btn} border border-white/15 text-zinc-400 hover:text-white`}><X size={15} /> Dismiss</button>
          </div>
        </>
      ) : m.status === "sent" && (
        <pre className="mt-5 whitespace-pre-wrap rounded-sm border border-neon/20 bg-neon/[0.04] p-4 font-body text-sm text-zinc-200">{m.draft}</pre>
      )}
    </div>
  );
};

export default function AdminInbox() {
  const { isOwner } = useAuth();
  const [status, setStatus] = useState({});
  const [filter, setFilter] = useState("drafted");
  const [items, setItems] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    api.get("/admin/gmail/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/admin/inbox", { params: { status: filter } }).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const flag = new URLSearchParams(window.location.search).get("gmail");
    if (flag === "connected") toast.success("Gmail connected — checking the inbox now…");
    if (flag === "error" || flag === "expired") toast.error("Google sign-in didn't complete. Please try again.");
    if (flag) window.history.replaceState({}, "", "/admin?tab=inbox");
    if (flag === "connected") sync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/gmail/sync");
      toast.success(`Inbox checked — ${data.drafted} draft${data.drafted === 1 ? "" : "s"} ready, ${data.skipped} skipped`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Inbox check failed"); }
    finally { setSyncing(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Gmail? Drafts already created will stay here.")) return;
    await api.post("/admin/gmail/disconnect"); load();
  };

  return (
    <div className="space-y-5" data-testid="admin-inbox">
      <ConnectCard status={status} onSync={sync} onDisconnect={disconnect} syncing={syncing} isOwner={isOwner} />
      <div className="flex gap-2">
        {[["drafted", `Awaiting approval${status.pending ? ` (${status.pending})` : ""}`], ["sent", "Sent"], ["skipped", "Skipped"], ["dismissed", "Dismissed"]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`inbox-filter-${k}`}
                  className={`rounded-full border px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors ${filter === k ? "border-neon text-neon" : "border-white/10 text-zinc-400 hover:text-white"}`}>{label}</button>
        ))}
      </div>
      {items.length === 0 && (
        <p className="rounded-lg border border-white/10 bg-obsidian-800 p-10 text-center font-mono text-sm text-zinc-500" data-testid="inbox-empty">
          {filter === "drafted" ? (status.connected ? "No enquiries waiting — nice." : "Connect Gmail to start drafting replies.") : "Nothing here yet."}
        </p>
      )}
      {items.map((m) => <MessageCard key={m.id} m={m} onChange={load} isOwner={isOwner} />)}
    </div>
  );
}
